import md5 from 'md5';
import { observable } from 'mobx';
import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/storage';
import 'firebase/auth';

import { noop } from '../utils';
import IHenshuPersistLayer, { IFile, HenshuUser } from './IHenshuPersistLayer';


export type FirebaseConfig = {
    apiKey: string, 
    authDomain: string,
    projectId: string,
    storageBucket: string,
    messagingSenderId: string,
    appId: string,
    measurementId: string,
};

export default class FirebasePersistLayer implements IHenshuPersistLayer {

    @observable
    lang: string = 'en';

    @observable
    loading: boolean = true;

    collection: string = 'henshu';

    readonly config: FirebaseConfig;
    readonly db: any;

    private _docExists: boolean = false;
    private _docRef: any;
    private _receiver: Function = noop;
    private _unsubscribeFromSnapshot: Function|null = null;
    private _batching: boolean = false;
    private _batched: object = {};
    private _perms: boolean = true;

    constructor(config: FirebaseConfig, lang?: string) {
        this.config = config;

        if (!firebase.apps.length) {
            firebase.initializeApp(this.config);
        }

        this.db = firebase.firestore();

        if (lang) {
            this.setLang(lang);
        }
    }

    authorize(): Promise<HenshuUser|null> {
        return new Promise(resolve => {
            const auth = firebase.auth();

            const unsub = auth.onAuthStateChanged((user: any) => {
                if (user === null) {
                    auth.setPersistence(firebase.auth.Auth.Persistence.LOCAL)
                        .then(() => {
                            return auth.signInWithPopup(new firebase.auth.GoogleAuthProvider());
                        });
                }
                else {
                    // We only need to listen for state change during this auth cycle
                    unsub();
                    this._subscribeToSnapshot();
                    this._perms = true;
                    resolve(user as HenshuUser);
                }
            });
        });
    }

    async deauthorize() {
        return await firebase.auth().signOut();
    }

    setCollection(collection: string) {
        this.collection = collection;
    }

    setLang(lang: string) {
        return new Promise<void>((resolve) => {
            this.loading = true;
            this.lang = lang;
            this._docRef = this.db.collection(this.collection).doc(this.lang);

            this._docRef.get().then((doc: any) => {
                this._docExists = doc.exists;
                resolve();
            })

            this._subscribeToSnapshot();
        });
    }

    async batch(batcher: Function) {
        this._batching = true;
        await batcher();
        this._batching = false;
        this.save(this._batched);
    }

    async save(data: object) {
        if (!this._batching) {
            if (this._perms) {
                try {
                    if (this._docExists) {
                        await this._docRef.update(data);
                    }
                    else {
                        await this._docRef.set(data);
                    }
                }
                catch(e) {
                    if (process.env.NODE_ENV === 'development') {
                        console.error(e);
                    }
                    this._perms = false;
                }
            }
        }
        else {
            this._batched = data;
        }
    }

    async upload(file: IFile, dir?: string): Promise<string> {
        const hash = md5(new Uint8Array(file.buffer));
        const path = dir ? `${dir.replace(/\/$/, '')}/${hash}.${file.extension}` : `${hash}.${file.extension}`;
        const storage = firebase.storage().ref().child(path);

        try {
            // Preventing duplicate uploads:
            // Will throw an exception if it doesn't already exist.
            return await storage.getDownloadURL();
        }
        catch(_) {
            await storage.put(file.buffer as Buffer, { contentType: file.mime });
            return await storage.getDownloadURL();
        }
    }
    
    receive(callback: Function) {
        this._receiver = callback;
    }

    private _subscribeToSnapshot() {
        if (this._unsubscribeFromSnapshot) {
            this._unsubscribeFromSnapshot();
        }

         this._unsubscribeFromSnapshot = this._docRef.onSnapshot((snapshot: any) => {
             const data = snapshot.data();
             if (data) {
                this._receiver(data as object);
             }
             else {
                 this._receiver({});
             }

             if (this.loading) {
                 this.loading = false;
             }
        });
    }

}
